import { MbscCalendarEvent, MbscCalendarEventData, MbscResource } from '@mobiscroll/react';
import React from 'react';
import { IconButton } from '@mui/material';
import { ContentCopy, Delete, Edit } from '@mui/icons-material';
import {
	EmployeeStatusIndicator,
	EmployeeStatusInfo,
} from '../../components/EmployeeStatusIndicator';
import moment from 'moment';

interface ScheduleEventProps {
	calendarType: string;
	calendarView?: string; // 'day', 'week', or 'workweek'
	data: MbscCalendarEventData;
	onEditClick?: (event: MbscCalendarEvent) => void;
	onDeleteClick?: (event: MbscCalendarEvent) => void;
	onCopyClick?: (event: MbscCalendarEvent) => void;
}

/**
 * Calculate the relative luminance of a color
 * Based on WCAG guidelines for color contrast
 */
const getColorLuminance = (hexColor: string): number => {
	// Remove # if present
	const hex = hexColor.replace('#', '');

	// Convert to RGB
	const r = parseInt(hex.substr(0, 2), 16) / 255;
	const g = parseInt(hex.substr(2, 2), 16) / 255;
	const b = parseInt(hex.substr(4, 2), 16) / 255;

	// Apply gamma correction
	const rLinear = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
	const gLinear = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
	const bLinear = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

	// Calculate relative luminance
	return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Determine if a background color is light or dark
 * Returns true if the color is light (needs dark text)
 */
const isLightColor = (hexColor: string): boolean => {
	if (!hexColor || hexColor === 'transparent') return false;
	try {
		const luminance = getColorLuminance(hexColor);
		// Threshold of 0.5 works well for most cases
		return luminance > 0.5;
	} catch (e) {
		// If there's an error parsing the color, default to white text
		return false;
	}
};

const getScheduleEventStyles = (original: MbscCalendarEvent) => {
	const dynamicStyle = original.added_by_mabel
		? { border: '2px solid var(--color-purple)' }
		: { borderColor: original?.background_color };

	// Determine text color based on background color brightness
	const backgroundColor = original?.background_color || '#446388';
	const textColor = isLightColor(backgroundColor) ? '#000000' : '#ffffff';

	return {
		cursor: 'pointer',
		backgroundColor: backgroundColor,
		color: textColor,
		...dynamicStyle,
	};
};

const getEventClassNames = (calendarType: string, original: MbscCalendarEventData['original']) => {
	return [
		'md-timeline-template-event',
		original?.overlap ? 'hasStripeBackground' : '',
		calendarType === 'request' ? 'requestsEventCustom' : '',
		'scheduleEvent',
		original?.role === undefined && calendarType === 'request' ? 'timeoffColor' : '',
	]
		.filter(Boolean)
		.join(' ');
};

export const ScheduleEvent: React.FC<ScheduleEventProps> = ({
	calendarType,
	calendarView,
	data,
	onEditClick,
	onDeleteClick,
	onCopyClick,
}) => {
	const { original } = data!;
	// console.log('ScheduleEvent original data:', original, data);

	const isRequest = original.role !== undefined || calendarType !== 'request';

	if (original?.shift_type?.id === 2 && original?.scheduled) return null;

	const handleEditClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent event from triggering calendar's onEventClick
		if (onEditClick && original) {
			onEditClick(original);
		}
	};

	const handleDeleteClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent event from triggering calendar's onEventClick
		if (onDeleteClick && original) {
			onDeleteClick(original);
		}
	};

	const handleCopyClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent event from triggering calendar's onEventClick
		if (onCopyClick && original) {
			onCopyClick(original);
		}
	};

	// Build employee status array for events
	// Calculate availability conflict directly from shift data
	const employeeStatuses: EmployeeStatusInfo[] = [];
	
	// Check for availability conflicts using shift timing and employeeAvailability data
	if (original?.employee && original?.startAt && original?.endAt) {
		const shiftStart = moment(original.startAt);
		const shiftEnd = moment(original.endAt);
		const employeeAvailability = (original as any).employeeAvailability;
		
		// Check employeeAvailability (recurring weekly pattern)
		if (employeeAvailability && employeeAvailability.length > 0) {
			const shiftDayOfWeek = shiftStart.day(); // 0 = Sunday, 1 = Monday, etc.
			const shiftStartTimeOnly = shiftStart.format('HH:mm:ss');
			const shiftEndTimeOnly = shiftEnd.format('HH:mm:ss');
			const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
			const shiftDayName = dayNames[shiftDayOfWeek];
			
			// Find availability for this shift's day of week
			const dayAvailabilities = employeeAvailability.filter((avail: any) => {
				if (!avail.day) return false;
				// The day field could be a string reference like "/api/days/2" or an object with @id
				const dayRef = typeof avail.day === 'string' ? avail.day : avail.day['@id'];
				// Extract day ID from reference (e.g., "/api/days/2" -> "2")
				const dayId = dayRef.split('/').pop();
				// Map day IDs to day names (1=Monday, 2=Tuesday, etc., 7=Sunday)
				const dayMap: { [key: string]: string } = {
					'1': 'Monday',
					'2': 'Tuesday', 
					'3': 'Wednesday',
					'4': 'Thursday',
					'5': 'Friday',
					'6': 'Saturday',
					'7': 'Sunday'
				};
				return dayMap[dayId] === shiftDayName;
			});
			
			if (dayAvailabilities.length > 0) {
				// Check if shift time falls within any availability window
				const isWithinAvailability = dayAvailabilities.some((avail: any) => {
					if (!avail.startAt || !avail.endAt) return false;
					
					// Parse availability times to extract time component only
					const availStartTimeOnly = moment(avail.startAt).format('HH:mm:ss');
					const availEndTimeOnly = moment(avail.endAt).format('HH:mm:ss');
					
					return shiftStartTimeOnly >= availStartTimeOnly && shiftEndTimeOnly <= availEndTimeOnly;
				});
				
				// If shift is NOT within any availability window, there's a conflict
				if (!isWithinAvailability) {
					const availTimes = dayAvailabilities.map((avail: any) => 
						`${moment(avail.startAt).format('h:mm A')} - ${moment(avail.endAt).format('h:mm A')}`
					).join(', ');
					
					employeeStatuses.push({
						type: 'not_available',
						label: 'Availability Conflict',
						details: `Available on ${shiftDayName}: ${availTimes}`,
						severity: 'error',
					});
				}
			}
		}
		
		// Check for time-off conflicts using employeeTimeOff data (specific date ranges)
		const employeeTimeOff = (original as any).employeeTimeOff;
		if (employeeTimeOff && employeeTimeOff.length > 0) {
			const shiftDate = shiftStart.format('YYYY-MM-DD');
			
			const matchingTimeOff = employeeTimeOff.find((timeOff: any) => {
				if (timeOff.startAt && timeOff.endAt) {
					// Extract only date parts for comparison (ignore time)
					const timeOffStartDate = moment(timeOff.startAt).format('YYYY-MM-DD');
					const timeOffEndDate = moment(timeOff.endAt).format('YYYY-MM-DD');
					
					// Check if shift date falls within time-off period (inclusive)
					return shiftDate >= timeOffStartDate && shiftDate <= timeOffEndDate;
				}
				return false;
			});
			
			if (matchingTimeOff) {
				const timeOffStart = moment(matchingTimeOff.startAt).format('MMM D, YYYY');
				const timeOffEnd = moment(matchingTimeOff.endAt).format('MMM D, YYYY');
				const timeOffMessage = timeOffStart === timeOffEnd 
					? `Employee has time off on ${timeOffStart}`
					: `Employee has time off: ${timeOffStart} - ${timeOffEnd}`;
				
				employeeStatuses.push({
					type: 'time_off',
					label: 'Time Off Scheduled',
					details: timeOffMessage,
					severity: 'error',
				});
			}
		}
	}
	// console.log('Employee statuses after availability/time-off check:', original?.employeeStatuses, employeeStatuses);
	
	// Include other statuses from employeeStatusData if available
	if (original?.employeeStatusData) {
		const statusData = original.employeeStatusData;
		
		// Day view: show daily overtime statuses
		if (calendarView === 'day') {
			if (statusData.isShiftCausingDailyOT) {
				const overtimeHours = statusData.todayOvertimeHours;
				employeeStatuses.push({
					type: 'in_overtime',
					label: 'In Daily Overtime',
					details: `This shift pushes employee ${overtimeHours.toFixed(1)} hours over daily threshold`,
					severity: 'error',
				});
			} else if (statusData.isApproachingDailyOvertime && statusData.dailyOTWarning) {
				// Only show if dailyOTWarning setting is configured
				employeeStatuses.push({
					type: 'approaching_overtime',
					label: 'Approaching Daily Overtime',
					details: `${statusData.todayHours.toFixed(1)} hours today (warning at ${statusData.dailyOTWarning} hours)`,
					severity: 'warning',
				});
			}

			if (statusData.isMinor) {
				employeeStatuses.push({
					type: 'minor',
					label: 'Minor Employee',
					details: 'Employee is under the age of minor',
					severity: 'info',
				});
			}
		}
		
		// Week view: show weekly overtime statuses
		if (calendarView === 'week' || calendarView === 'workweek') {
			if (statusData.isShiftCausingWeeklyOT) {
				const overtimeHours = statusData.overtimeHours;
				employeeStatuses.push({
					type: 'in_overtime',
					label: 'In Weekly Overtime',
					details: `This shift pushes employee ${overtimeHours.toFixed(1)} hours over weekly threshold`,
					severity: 'error',
				});
			} else if (statusData.isApproachingWeeklyOvertime && statusData.weeklyOTWarning) {
				// Only show if weeklyOTWarning setting is configured
				employeeStatuses.push({
					type: 'approaching_overtime',
					label: 'Approaching Weekly Overtime',
					details: `${statusData.totalHours.toFixed(1)} hours this week (warning at ${statusData.weeklyOTWarning} hours)`,
					severity: 'warning',
				});
			}
			if (statusData.isMinor) {
				employeeStatuses.push({
					type: 'minor',
					label: 'Minor Employee',
					details: 'Employee is under the age of minor',
					severity: 'info',
				});
			}
		}
	}

	return (
		<div
			className={getEventClassNames(calendarType, original)}
			style={getScheduleEventStyles(original)}
		>
			<div className="md-custom-event-wrapper">
				<div className="md-custom-event-details">
					<div className="md-custom-event-header">
						<div className="md-custom-event-title">
							{isRequest ? (
								<div className="shift-info">
									<div
										className="shift-time"
										style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
									>
										<span>
											{data.start} - {data.end}
										</span>
										{!original?.published && (
											<div
												style={{
													fontSize: '0.75rem',
													color: '#ffc107',
													fontWeight: 500,
													display: 'flex',
													alignItems: 'center',
													gap: '4px',
												}}
											>
												<span style={{ fontSize: '0.9rem' }}>●</span>
												Unpublished
											</div>
										)}
									</div>
									<div className="employee-name">
										{original?.employee
											? `${original.employee.firstName} ${original.employee.lastName}`
											: 'House Shift'}
										{employeeStatuses.length > 0 && (
											<EmployeeStatusIndicator
												statuses={employeeStatuses}
												employeeName={
													original?.employee
														? `${original.employee.firstName} ${original.employee.lastName}`
														: undefined
												}
											/>
										)}
									</div>
									{original?.jobCode && (
										<div className="job-code">
											{original.jobCode.jobNumber
												? `${original.jobCode.jobNumber} - ${original.jobCode.label}`
												: original.jobCode.label}
										</div>
									)}
								</div>
							) : (
								'Time off'
							)}
						</div>
						{(onEditClick || onDeleteClick || onCopyClick) && (
							<div className="action-buttons">
								{onEditClick && (
									<IconButton
										onClick={handleEditClick}
										size="small"
										style={{
											backgroundColor: 'white',
											marginRight: '4px',
											color: 'rgb(68, 99, 136)',
										}}
										className="edit-btn"
									>
										<Edit fontSize="small" />
									</IconButton>
								)}
								{onCopyClick && (
									<IconButton
										onClick={handleCopyClick}
										size="small"
										style={{
											backgroundColor: 'white',
											marginRight: '4px',
											color: 'rgb(68, 99, 136)',
										}}
										className="copy-btn"
									>
										<ContentCopy fontSize="small" />
									</IconButton>
								)}
								{onDeleteClick && (
									<IconButton
										onClick={handleDeleteClick}
										size="small"
										style={{ backgroundColor: 'white', color: '#d32f2f' }}
										className="delete-btn"
									>
										<Delete fontSize="small" />
									</IconButton>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

interface ScheduleResourceProps {
	resource: MbscResource;
	calendarType: string;
}

export const ScheduleResource: React.FC<ScheduleResourceProps> = ({
	resource,
	calendarType: _calendarType,
}) => {
	// Check if this is an employee resource (has employee data) or job code resource
	const isEmployeeResource = resource.employee !== undefined;
	const isOpenShift = (resource as any)?.isOpenShift;

	// Build status array for employee resources
	const employeeStatuses: EmployeeStatusInfo[] = [];
	if (isEmployeeResource && !isOpenShift) {
		const resourceWithStatus = resource as any;

		// Check for overtime
		if (resourceWithStatus.hasOvertime) {
			const overtimeType = resourceWithStatus.hasDailyOvertime
				? 'Daily'
				: resourceWithStatus.hasWeeklyOvertime
					? 'Weekly'
					: '';

			// Always use overtimeHours which already has the correct calculation (hours - threshold)
			const overtimeHours = resourceWithStatus.overtimeHours;

			employeeStatuses.push({
				type: 'in_overtime',
				label: `In ${overtimeType} Overtime`,
				details: `Working ${overtimeHours.toFixed(1)} hours over ${overtimeType.toLowerCase()} threshold`,
				severity: 'error',
			});
		} else if (resourceWithStatus.isApproachingWeeklyOvertime && resourceWithStatus.weeklyOTWarning) {
			// Week view: only show approaching weekly overtime if setting is configured
			employeeStatuses.push({
				type: 'approaching_overtime',
				label: 'Approaching Weekly Overtime',
				details: `${resourceWithStatus.totalHours.toFixed(1)} hours this week (warning at ${resourceWithStatus.weeklyOTWarning} hours)`,
				severity: 'warning',
			});
		}

		// Check for time off
		if (resourceWithStatus.hasTimeOff) {
			employeeStatuses.push({
				type: 'time_off',
				label: 'Time Off Scheduled',
				details: 'Employee has approved time off during this week',
				severity: 'error',
			});
		}

		// Check for availability issues
		if (resourceWithStatus.hasAvailabilityIssue) {
			employeeStatuses.push({
				type: 'not_available',
				label: 'Availability Conflict',
				details: 'One or more shifts conflict with employee availability',
				severity: 'error',
			});
		}

		// Check for minor
		if (resourceWithStatus.isMinor) {
			employeeStatuses.push({
				type: 'minor',
				label: 'Minor Employee',
				details: 'Employee is under the age of minor',
				severity: 'info',
			});
		}
	}
	return (
		<div className={`md-work-week-cont`}>
			<div className="md-work-week-name">
				{isEmployeeResource || isOpenShift ? (
					// Week view: Show employee name with total hours
					<div className="employee-resource">
						<div className="employee-name" style={{ display: 'flex', alignItems: 'center' }}>
							<span>{(resource as any)?.displayName || resource.name}</span>
							{employeeStatuses.length > 0 && (
								<EmployeeStatusIndicator
									statuses={employeeStatuses}
									employeeName={(resource as any)?.displayName}
								/>
							)}
						</div>
						{(resource as any)?.totalHours !== undefined && (
							<div className="employee-hours">{(resource as any).totalHours.toFixed(1)} hours</div>
						)}
					</div>
				) : (
					// Day view: Show job code (existing logic)
					<div className="jobcode-resource">{resource.name}</div>
				)}
			</div>
		</div>
	);
};

export default ScheduleEvent;
